import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { editSection, getSection } from "store/actions";
import Loader from "components/shared/Loader";
import ImageUpload from "components/shared/ImageUpload";
import { Input, Textarea } from "components/shared/FormComponents";
import SaveChangesButton from "components/shared/SaveChangesButton";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";

const Section = () => {
    const { formatMessage, locale } = useIntl();
    const dispatch = useDispatch();
    const { loading, error, section } = useSelector((state) => state.section);
    const [selectedMedia, setSelectedMedia] = useState({
        preview: null,
        path: "",
    });
    const [mediaType, setMediaType] = useState("image"); // 'image', 'video', or 'youtube'
    const {
        register,
        reset,
        handleSubmit,
        formState: { errors },
    } = useForm();

    useEffect(() => {
        dispatch(
            getSection({
                page: "home",
                slug: "about",
            })
        );
    }, [dispatch]);

    useEffect(() => {
        reset({
            heading: section?.heading,
            subTitle: section?.subTitle,
            description: section?.description,
            image: section?.image,
            youtubeLink: section?.youtubeLink, // Reset youtubeLink to an empty string by default
        });

        setSelectedMedia({
            preview: null,
            path: section?.image,
        });

        const determineInitialMediaType = () => {
            if (section?.youtubeLink) return "youtube";
            if (section?.image?.match(/\.(mp4|webm|ogg)$/i)) return "video";
            return "image"; // Default to "image" if neither is available
        };
        setMediaType(determineInitialMediaType());
    }, [reset, section]);
    useEffect(() => {
        if (error) {
            toastErrorMessage(
                formatMessage({ id: "sendingErrorMessage" }),
                locale
            );
        }
    }, [error, formatMessage, locale]);

    const onSubmit = (data) => {
        if (mediaType === "youtube" && !data.youtubeLink) {
            toastErrorMessage(
                formatMessage({ id: "youtubeErrorMessage" }),
                locale
            );
            return;
        }

        if (mediaType !== "youtube" && !selectedMedia?.path) {
            toastErrorMessage(
                formatMessage({ id: "mediaErrorMessage" }),
                locale
            );
            return;
        }

        // Set fields conditionally based on mediaType
        if (mediaType === "youtube") {
            data.image = ""; // Clear the image field
        } else {
            data.image = selectedMedia?.path; // Set image path
            data.youtubeLink = ""; // Clear youtubeLink
        }

        dispatch(
            editSection({
                slug: "about",
                page: "home",
                data: { section: data },
            })
        );
    };

    if (loading) return <Loader />;

    return (
        <>
            <div className="acc-form">
                <div className="card">
                    <div className="card-head">
                        <h4>
                            <FormattedMessage id={"about"} />
                        </h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Col xl={8} md={12}>
                                {/* Media Type Selector */}
                                <div className="form-group required">
                                    <h5>
                                        <FormattedMessage id={"mediaType"} />
                                    </h5>
                                    <select
                                        value={mediaType}
                                        onChange={(e) =>
                                            setMediaType(e.target.value)
                                        }
                                        className="form-control"
                                    >
                                        <option value="image">Image</option>
                                        <option value="video">Video</option>
                                        <option value="youtube">
                                            YouTube Link
                                        </option>
                                    </select>
                                </div>

                                {/* Media Input */}
                                {mediaType === "image" ||
                                mediaType === "video" ? (
                                    <div className="form-group required">
                                        <h5>
                                            <FormattedMessage
                                                id={
                                                    mediaType === "image"
                                                        ? "image"
                                                        : "video"
                                                }
                                            />
                                        </h5>
                                        <ImageUpload
                                            selectedImage={selectedMedia}
                                            setSelectedImage={setSelectedMedia}
                                            accept={
                                                mediaType === "image"
                                                    ? "image/*"
                                                    : "video/*"
                                            }
                                        />
                                    </div>
                                ) : (
                                    <div className="form-group required">
                                        <Input
                                            langId="youtubeLink"
                                            name="youtubeLink"
                                            register={register}
                                            errors={errors}
                                        />
                                    </div>
                                )}

                                {/* Heading and SubTitle */}
                                <Row>
                                    <Col lg={6} xs={12}>
                                        <Input
                                            langId="title"
                                            name="heading.ar"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col lg={6} xs={12}>
                                        <Input
                                            langId="title"
                                            name="heading.en"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col lg={6} xs={12}>
                                        <Input
                                            langId="subTitle"
                                            name="subTitle.ar"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col lg={6} xs={12}>
                                        <Input
                                            langId="subTitle"
                                            name="subTitle.en"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col lg={6} xs={12}>
                                        <Textarea
                                            langId="description"
                                            name="description.ar"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                    <Col lg={6} xs={12}>
                                        <Textarea
                                            langId="description"
                                            name="description.en"
                                            register={register}
                                            errors={errors}
                                        />
                                    </Col>
                                </Row>

                                <SaveChangesButton />
                            </Col>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Section;
